import { type ChangeEvent, forwardRef, useEffect } from 'react';
import { useFormContext, type UseFormRegisterReturn, useWatch } from 'react-hook-form';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { type z } from 'zod';
import { CaretDownIcon, MagnifyingGlassIcon } from '@/icons';
import { SignSelectSchoolGrade, SignSelectSchoolType } from '@/module/auth/sign-up';
import { type ProfileEditFormData, type StudentEditFormData, type TeacherEditFormData } from '@/module/profile/edit/schema';
import { useCountryName } from '@/shared/i18n/displayName';
import { type UserType } from '@/types';
import { Input } from '@/ui';
import { Button, confirmDialog } from '@/ui_v3';
import { combinePathAndQuery } from '@/utils/combinePathAndQuery';
import { isAllGrades } from '@/views/build/util';
import { openSchoolSearchModal } from '../modal/schoolSearch/SchoolSearchModal';
import { type NormalSchema, type StudentSchema, type TeacherSchema } from '../schema';
import { WithLabel, type WithLabelProps } from './WithLabel';

/**
 * 회원가입 폼에 들어가는 Input 컴포넌트 모음
 * 유저 타입 입력, 국가 입력, 학교 검색 및 선택, 학교 수기 입력, 직책 입력, 전화번호 입력, 참조 이메일 입력
 */

export const INPUT_MAX_LENGTH = 50;
export const SignInputUserType = ({
  type,
  isTypeChangeable = true
}: {
  type: UserType;
  isTypeChangeable?: boolean;
}) => {
  const router = useRouter();
  const {
    t
  } = useTranslation(['common']);
  const typeValue: Record<UserType, string> = {
    student: t('signup.selectType.studentTitle'),
    teacher: t('signup.selectType.educationWorker'),
    normal: t('signup.selectType.normalTitle'),
    company: t('signup.selectType.companyTitle'),
    unknown: ''
  };
  const handleChangeSignUpType = async () => {
    const confirm = await confirmDialog.openAsync({
      header: {
        title: t('account.signup.confirmType')
      },
      body: t('account.signup.resetWarmType'),
      confirmText: t('account.signup.change'),
      confirmIntent: 'destructive',
      cancelText: t('global.cancel.button')
    });
    if (confirm) {
      void router.push(combinePathAndQuery('/sign-up/type', router.query, ['type']));
    }
  };
  return <WithLabel label={t('account.signup.accountType')} data-sentry-element="WithLabel" data-sentry-component="SignInputUserType" data-sentry-source-file="SignInput.tsx">
      <Input className={'border-input h-[47px] w-full rounded-sm bg-alternative'} value={typeValue[type]} readOnly rightSlot={isTypeChangeable && <Button intent="neutral" className={'border-transparent bg-transparent'} size="sm" onClick={handleChangeSignUpType}>
              {t('account.signup.changeType')}
            </Button>} data-sentry-element="Input" data-sentry-source-file="SignInput.tsx" />
    </WithLabel>;
};
export const SignInputCountry = () => {
  const {
    t
  } = useTranslation(['common']);
  const countryName = useCountryName();
  const router = useRouter();
  const {
    getValues
  } = useFormContext<z.infer<typeof NormalSchema> | ProfileEditFormData>();
  const isSignUp = router.pathname.includes('/sign-up');
  const countryCode = getValues('countryCode');

  // country 네임스페이스에서 조합, 미등록 코드는 서버가 준 countryName/코드로 폴백.
  const country = countryName(countryCode, getValues('countryName') || countryCode);
  return <WithLabel label={t('account.signup.country')} errorMessage={<span className={'text-assistive'}>
          {isSignUp ? t('account.signup.changeInquiry') : t('profileEdit.country.changeInquiry')}
        </span>} data-sentry-element="WithLabel" data-sentry-component="SignInputCountry" data-sentry-source-file="SignInput.tsx">
      <Input className={'border-input h-[47px] w-full rounded-sm bg-alternative'} value={country} readOnly data-sentry-element="Input" data-sentry-source-file="SignInput.tsx" />
    </WithLabel>;
};

/**
 *  학교 검색 및 선택 (KR 대상)
 * */
export const SignInputSchool = () => {
  const formControl = useFormContext<z.infer<typeof StudentSchema | typeof TeacherSchema> | TeacherEditFormData>();
  const {
    control,
    setValue,
    getValues,
    formState: {
      errors
    }
  } = formControl;
  const {
    t
  } = useTranslation(['common']);
  const errorMessage = errors.schoolName || errors.schoolTypeCode || errors.schoolGradeCode ? t('account.signup.requiredField') : undefined;
  const schoolName = useWatch({
    control,
    name: 'schoolName'
  });
  const schoolGrade = useWatch({
    control,
    name: 'schoolGrade'
  });
  const schoolType = useWatch({
    control,
    name: 'schoolType'
  });
  const noGrade = schoolType === 'COLLEGE' || schoolType === 'OTHER' || schoolType === 'KINDERGARTEN';
  const handleOpenSchoolSearch = () => {
    const countryCode = getValues('countryCode');
    openSchoolSearchModal({
      countryCode: countryCode || 'KR',
      // 모달은 자체 임시 폼이라 바깥 폼 값이 안 넘어간다 → 학년 셀렉 분기용으로 명시 전달.
      userType: getValues('userType'),
      onConfirm: data => {
        // 확인 버튼 클릭 시에만 실제 폼 업데이트
        setValue('schoolName', data.schoolName, {
          shouldValidate: true,
          shouldDirty: true
        });
        setValue('schoolType', data.schoolType, {
          shouldValidate: true,
          shouldDirty: true
        });
        setValue('schoolGrade', data.schoolGrade, {
          shouldValidate: true,
          shouldDirty: true
        });
        setValue('verifiedSchool', data.verifiedSchool, {
          shouldValidate: true,
          shouldDirty: true
        });
        setValue('schoolTypeCode', data.schoolTypeCode, {
          shouldValidate: true,
          shouldDirty: true
        });
        setValue('schoolGradeCode', data.schoolGradeCode, {
          shouldValidate: true,
          shouldDirty: true
        });
      }
    });
  };
  return <WithLabel label={t('global.school')} errorMessage={errorMessage} data-sentry-element="WithLabel" data-sentry-component="SignInputSchool" data-sentry-source-file="SignInput.tsx">
      <div className="flex flex-col gap-xs">
        <div className="flex gap-xs">
          <Input className={'border-input h-[47px] w-full rounded-sm bg-alternative'} readOnly placeholder={t('signup.school.searchPlaceholder')} {...formControl.register('schoolName')} value={schoolName || ''} data-sentry-element="Input" data-sentry-source-file="SignInput.tsx" />
          <Button intent="alternative" variant="outlined" size="lg" leftSlot={<MagnifyingGlassIcon size="md" fill={ICON_COLOR.strong} />} className="shrink-0" onClick={handleOpenSchoolSearch} data-sentry-element="Button" data-sentry-source-file="SignInput.tsx">
            {t('signup.school.searchButton')}
          </Button>
        </div>
        <Input className={'border-input h-[47px] w-full rounded-sm bg-alternative'} readOnly placeholder={t('signup.school.gradePlaceholder')} value={noGrade ? t('signup.school.noGrade') : schoolGrade ? isAllGrades(schoolGrade) ? t('common:school.info.allGrades') : `${t('common:school.info.nGrade', {
        n: schoolGrade
      })}` : ''} rightSlot={<CaretDownIcon fill={ICON_COLOR.disabled} />} data-sentry-element="Input" data-sentry-source-file="SignInput.tsx" />
      </div>
    </WithLabel>;
};

/**
 *  학교 수기 입력 (KR 제외 국가 대상)
 * */
export const SignInputSchoolManual = () => {
  const {
    register,
    control,
    setValue,
    clearErrors,
    formState: {
      errors
    }
  } = useFormContext<z.infer<typeof StudentSchema | typeof TeacherSchema> | TeacherEditFormData | StudentEditFormData>();
  const {
    t
  } = useTranslation(['common']);

  // 수기 입력의 경우 verifiedSchool을 null로 설정
  useEffect(() => {
    setValue('verifiedSchool', null);
  }, [setValue]);
  const errorMessage = !!errors.schoolName || !!errors.schoolTypeCode || !!errors.schoolGradeCode ? t('account.signup.requiredField') : undefined;
  const schoolType = useWatch({
    control,
    name: 'schoolType'
  });
  const schoolGrade = useWatch({
    control,
    name: 'schoolGrade'
  });
  const handleSchoolTypeChange = (value: string) => {
    clearErrors(['schoolType', 'schoolTypeCode']);
    setValue('schoolType', value, {
      shouldValidate: true,
      shouldDirty: true
    });
    if (value === 'COLLEGE' || value === 'OTHER' || value === 'KINDERGARTEN') {
      setValue('schoolGrade', '0', {
        shouldValidate: true,
        shouldDirty: true
      });
      setValue('schoolGradeCode', 'noGradeCode', {
        shouldValidate: true,
        shouldDirty: true
      });
    } else {
      setValue('schoolGrade', '', {
        shouldValidate: true,
        shouldDirty: true
      });
      setValue('schoolGradeCode', '', {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  };
  const handleSchoolGradeChange = (value: string) => {
    clearErrors(['schoolGrade', 'schoolGradeCode', 'schoolTypeCode']);
    setValue('schoolGrade', value, {
      shouldValidate: true,
      shouldDirty: true
    });
  };
  return <WithLabel label={t('global.school')} className="gap-xs" errorMessage={errorMessage} data-sentry-element="WithLabel" data-sentry-component="SignInputSchoolManual" data-sentry-source-file="SignInput.tsx">
      <Input className="border-input-border h-[47px] w-full rounded-sm" placeholder={t('signup.school.namePlaceholder')} {...register('schoolName')} maxLength={INPUT_MAX_LENGTH} data-sentry-element="Input" data-sentry-source-file="SignInput.tsx" />
      <div className={'flex w-full gap-xs'}>
        <SignSelectSchoolType value={schoolType ?? ''} onValueChange={handleSchoolTypeChange} data-sentry-element="SignSelectSchoolType" data-sentry-source-file="SignInput.tsx" />
        <SignSelectSchoolGrade value={schoolGrade?.toString()} onValueChange={handleSchoolGradeChange} data-sentry-element="SignSelectSchoolGrade" data-sentry-source-file="SignInput.tsx" />
      </div>
    </WithLabel>;
};
export const SignInputPhone = (props: {
  type: Exclude<UserType, 'student' | 'unknown'>;
}) => {
  const {
    register,
    setValue,
    formState: {
      errors
    }
  } = useFormContext<z.infer<typeof NormalSchema>>();
  const {
    t
  } = useTranslation(['common']);
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue('phoneNumber', event.target.value.replace(/[^0-9]/g, '').slice(0, 30), {
      shouldDirty: true,
      shouldValidate: true
    });
  };
  const helperText = (() => {
    switch (props.type) {
      case 'teacher':
        return t('account.signup.phoneHelperText1');
      case 'normal':
        return t('account.signup.phoneHelperText2');
      case 'company':
        return t('account.signup.phoneHelperText3');
      default:
        return '';
    }
  })();
  const isError = (errors?.phoneNumber?.message?.length || 0) > 0;
  return <WithLabel label={t('account.signup.phoneNumber')} subLabel={t('account.signup.select')} helperText={helperText} errorMessage={isError && t('account.signup.minLength8')} data-sentry-element="WithLabel" data-sentry-component="SignInputPhone" data-sentry-source-file="SignInput.tsx">
      <Input error={isError} maxLength={INPUT_MAX_LENGTH} className={`border-input-border h-[47px] w-full rounded-sm ${isError && 'border-[#FFACB6] focus-within:border-[#FFACB6]'}`} placeholder={t('account.signup.inputNoSpecialChars')} {...register('phoneNumber')} onChange={handleChange} data-sentry-element="Input" data-sentry-source-file="SignInput.tsx" />
    </WithLabel>;
};

/**
 *  직책 입력
 * */
export const SignInput = forwardRef<HTMLInputElement, Omit<UseFormRegisterReturn, 'ref'> & {
  label: WithLabelProps;
  isError?: boolean;
}>((props, ref) => {
  const {
    t
  } = useTranslation(['common']);
  const errorMessage = props.isError ? t('account.signup.requiredField') : undefined;
  return <WithLabel {...props.label} errorMessage={errorMessage}>
      <Input error={props.isError} maxLength={INPUT_MAX_LENGTH} ref={ref} className={`border-input-border h-[47px] w-full rounded-sm ${props.isError ? 'border-[#FFACB6] focus-within:border-[#FFACB6]' : ''}`} placeholder={t('account.signup.correctName')} {...props} />
    </WithLabel>;
});
SignInput.displayName = 'SignInput';